import { useState, useEffect } from "react";
import { Shield, FileText } from "lucide-react";
import { getSupabase } from "@dwelldrop/supabase";
import { useVenueAuth } from "../../contexts/VenueAuthContext";
import type {
  VenueConsentData,
  VenueConsentType,
} from "../../hooks/useVenueConsentGate";
import ConsentDocumentModal from "./auth/ConsentDocumentModal";

const VENUE_SUPABASE_STORAGE_KEY = "venuehub_venue_token";

// Consents whose stored name doesn't read well in the UI.
const CONSENT_DISPLAY_NAMES: Record<string, string> = {
  terms_and_condition_venue: "Standard Merchant Terms",
};

function formatConsentName(name: string): string {
  const override = CONSENT_DISPLAY_NAMES[name];
  if (override) return override;

  return name
    .replace(/_venue$/, "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const HIDDEN_VENUE_LEGAL_NAMES = new Set([
  "terms_and_condition_venue",
  "privacy_policy_venue",
]);

function legalDocumentModalForConsentName(
  name: string,
): "terms" | "privacy" | null {
  if (name === "terms_and_condition_venue") return "terms";
  if (name === "privacy_policy_venue") return "privacy";
  return null;
}

// A privacy policy is acknowledged rather than agreed to; everything else
// (terms and conditions included) is an agreement.
function consentActionLabel(name: string, accepted: boolean): string {
  if (name === "privacy_policy_venue") {
    return accepted ? "Acknowledged" : "Acknowledge";
  }
  return accepted ? "Agreed" : "Agree";
}

// The venue legal consents carry bespoke copy, worded differently for a first
// confirmation than for re-confirming a revised version. Anything else falls
// back to the generic notice.
function consentNoticeText(
  name: string,
  consentedBefore: boolean,
): string | null {
  if (name === "terms_and_condition_venue") {
    return consentedBefore
      ? "Agreement to the updated Standard Merchant Terms is required to continue using VenueHub."
      : "Agreement to the Standard Merchant Terms is required to continue using VenueHub.";
  }
  if (name === "privacy_policy_venue") {
    return consentedBefore
      ? "Acknowledgement of the updated Privacy Policy is required to continue using VenueHub."
      : "Acknowledgement of the Privacy Policy is required to continue using VenueHub.";
  }
  return null;
}

function buildState(data: VenueConsentData): {
  visibleTypes: VenueConsentType[];
  outdatedNames: Set<string>;
  previouslyConsentedNames: Set<string>;
  initial: Record<string, boolean>;
} {
  const activeByName = new Map<string, number>();
  for (const c of data.consents) {
    if (c.status === "active") {
      activeByName.set(c.consent_name, c.version);
    }
  }

  // Any non-null status means a record exists for that consent — active,
  // withdrawn or superseded — so the venue has agreed to it at some point.
  const previouslyConsentedNames = new Set<string>();
  for (const c of data.consents) {
    if (c.status) {
      previouslyConsentedNames.add(c.consent_name);
    }
  }

  const isFirstTime = activeByName.size === 0;
  const hasAnyActive = activeByName.size > 0;

  const updatedTypeNames = new Set<string>();
  if (!isFirstTime) {
    for (const t of data.types) {
      const userVer = activeByName.get(t.name);
      if (userVer !== undefined && userVer !== t.version) {
        updatedTypeNames.add(t.name);
      }
    }
    for (const t of data.types) {
      const userVer = activeByName.get(t.name);
      if (userVer === undefined && t.is_required) {
        updatedTypeNames.add(t.name);
      }
    }
  }

  const outdatedHiddenNames = new Set<string>();
  if (!isFirstTime) {
    for (const t of data.types) {
      if (HIDDEN_VENUE_LEGAL_NAMES.has(t.name)) {
        const userVer = activeByName.get(t.name);
        if (userVer === undefined || userVer !== t.version) {
          outdatedHiddenNames.add(t.name);
        }
      }
    }
  }

  const baseVisibleTypes = isFirstTime
    ? data.types
    : data.types.filter(
        (t) =>
          !HIDDEN_VENUE_LEGAL_NAMES.has(t.name) ||
          outdatedHiddenNames.has(t.name),
      );

  const visibleTypes =
    hasAnyActive && updatedTypeNames.size > 0
      ? baseVisibleTypes.filter((t) => updatedTypeNames.has(t.name))
      : baseVisibleTypes;

  const outdatedNames = new Set<string>();
  for (const t of visibleTypes) {
    const userVer = activeByName.get(t.name);
    if (userVer !== undefined && userVer !== t.version) {
      outdatedNames.add(t.name);
    }
    if (userVer === undefined && t.is_required) {
      outdatedNames.add(t.name);
    }
  }

  // Consent must be an explicit opt-in, so every toggle starts off — including
  // required ones. The user has to turn each on before Continue will advance.
  const initial: Record<string, boolean> = {};
  for (const ct of visibleTypes) {
    initial[ct.name] = false;
  }

  return { visibleTypes, outdatedNames, previouslyConsentedNames, initial };
}

type VenueConsentModalProps = {
  consentData: VenueConsentData;
  onComplete: () => void;
};

export default function VenueConsentModal({
  consentData,
  onComplete,
}: VenueConsentModalProps) {
  const { signOut } = useVenueAuth();

  const [consentTypes, setConsentTypes] = useState<VenueConsentType[]>([]);
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});
  const [outdatedNames, setOutdatedNames] = useState<Set<string>>(new Set());
  const [previouslyConsented, setPreviouslyConsented] = useState<Set<string>>(
    new Set(),
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorModal, setErrorModal] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [activeModal, setActiveModal] = useState<"terms" | "privacy" | null>(
    null,
  );

  useEffect(() => {
    const {
      visibleTypes,
      outdatedNames: od,
      previouslyConsentedNames: seen,
      initial,
    } = buildState(consentData);
    setConsentTypes(visibleTypes);
    setOutdatedNames(od);
    setPreviouslyConsented(seen);
    setAccepted(initial);
    setCurrentStep(0);
    setIsError(false);
    setErrorModal(null);
  }, [consentData]);

  const requiredConsentTypes = consentTypes.filter((ct) => ct.is_required);

  const submit = async (): Promise<void> => {
    const requiredOk = requiredConsentTypes.every((ct) => !!accepted[ct.name]);
    if (!requiredOk) {
      setErrorModal({
        title: "Required consents not accepted",
        message:
          "You must accept all required consents to continue. Please review the consents and enable the required ones.",
      });
      setIsError(true);
      return;
    }

    setSubmitting(true);
    setErrorModal(null);

    const supabase = getSupabase(VENUE_SUPABASE_STORAGE_KEY);

    const visibleNameSet = new Set(consentTypes.map((ct) => ct.name));
    const acceptedNames = Object.entries(accepted)
      .filter(([name, v]) => v && visibleNameSet.has(name))
      .map(([name]) => name);

    const revokedNames = Object.entries(accepted)
      .filter(([name, v]) => !v && visibleNameSet.has(name))
      .map(([name]) => name);

    const { error: fnError } = await supabase.functions.invoke("record-consent", {
      body: {
        consent_names: acceptedNames,
        ...(revokedNames.length > 0 && { revoked_names: revokedNames }),
      },
    });

    if (fnError) {
      setErrorModal({ title: "Failed to save consent", message: fnError.message });
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onComplete();
  };

  const toggle = (name: string) => {
    setAccepted((prev) => ({ ...prev, [name]: !prev[name] }));
    setIsError(false);
    setErrorModal(null);
  };

  const handleContinue = () => {
    const step = consentTypes[currentStep];
    if (step?.is_required && !accepted[step.name]) {
      setErrorModal({
        title: "Consent required",
        message:
          "You must accept this consent to continue. Please enable it to proceed.",
      });
      setIsError(true);
      return;
    }
    if (currentStep === consentTypes.length - 1) {
      void submit();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  if (consentTypes.length === 0) {
    return null;
  }

  const ct = consentTypes[currentStep];
  if (!ct) return null;

  const isLastStep = currentStep === consentTypes.length - 1;
  const isEnabled = accepted[ct.name] ?? false;
  const noticeText = consentNoticeText(
    ct.name,
    previouslyConsented.has(ct.name),
  );
  const progress = ((currentStep + 1) / consentTypes.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="border-b px-6 pb-4 pt-6">
          <div className="mb-2 flex items-center gap-2">
            <Shield className="h-8 w-8 text-blue-600" />
            <div>
              <h2 className="flex-start text-xl font-semibold text-gray-900">
                Privacy &amp; Consent
              </h2>
              <p className="mb-3 text-sm text-gray-500">
                Step {currentStep + 1} of {consentTypes.length}
              </p>
            </div>
          </div>
          <div className="h-1 w-full rounded-full bg-gray-200">
            <div
              className="h-1 rounded-full bg-blue-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step content */}
        <div className="px-6">
          <div className="py-4">
            {/* Icon + title + description */}
            <div className="mb-4 flex items-start gap-3">
              <div
                className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-lg ${ct.is_required ? "bg-green-200" : "bg-gray-100 text-blue-600"}`}
              >
                <FileText
                  className={`h-8 w-8 ${ct.is_required ? "text-green-600" : "text-blue-600"}`}
                />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {formatConsentName(ct.name)}
                  </h3>
                  {ct.is_required && (
                    <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                      Required
                    </span>
                  )}
                  {outdatedNames.has(ct.name) && ct.version > 1 && (
                    <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
                      Updated
                    </span>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      const legalModal = legalDocumentModalForConsentName(
                        ct.name,
                      );
                      if (legalModal) setActiveModal(legalModal);
                    }}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    View Detail
                  </button>
                </div>
              </div>
            </div>

            {/* Toggle row */}
            <div className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5">
              <div className="flex items-center gap-2">
                {isEnabled ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-check-circle2 h-5 w-5 text-green-600"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="m9 12 2 2 4-4"></path>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-xcircle h-5 w-5 text-gray-400"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="m15 9-6 6"></path>
                    <path d="m9 9 6 6"></path>
                  </svg>
                )}
                <span className="font-medium text-gray-900">
                  {consentActionLabel(ct.name, isEnabled)}
                </span>
              </div>
              {/* Toggle switch */}
              <button
                type="button"
                role="switch"
                aria-checked={isEnabled}
                disabled={submitting}
                onClick={() => toggle(ct.name)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                  isEnabled ? "bg-green-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isEnabled ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Privacy rights — only meaningful for consents the venue can decline */}
            {!ct.is_required && (
              <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs text-gray-600">
                  <strong>Your Privacy Rights:</strong> You can change these
                  settings anytime in Settings. We&apos;ll only process your
                  data if you consent, except for required features.
                </p>
              </div>
            )}
            {outdatedNames.size > 0 && (
              <div className="mt-2 rounded-lg border border-orange-200 bg-orange-50 p-3">
                <p className="text-xs text-orange-700">
                  {noticeText ?? (
                    <>
                      <strong>Policy Updated:</strong> The policies you
                      previously confirmed have been updated. Please press
                      Continue to reconfirm.
                    </>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Required consent error overlay */}
        {errorModal && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setErrorModal(null)}
          >
            <div
              className="w-full max-w-sm space-y-3 rounded-xl bg-white p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-sm font-bold text-gray-800">
                {errorModal.title}
              </h3>
              <p className="text-xs text-gray-500">{errorModal.message}</p>
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    setErrorModal(null);
                    await signOut();
                  }}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                >
                  Decline &amp; Sign Out
                </button>
                <button
                  type="button"
                  onClick={() => setErrorModal(null)}
                  className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 p-6">
          <div />
          <button
            type="button"
            disabled={submitting || isError || (ct.is_required && !isEnabled)}
            onClick={handleContinue}
            className="rounded-lg bg-blue-600 px-6 py-2 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting
              ? "Saving..."
              : isLastStep
                ? "Confirm & Continue"
                : "Continue"}
          </button>
        </div>
      </div>

      <ConsentDocumentModal
        activeModal={activeModal}
        onClose={() => setActiveModal(null)}
        backdropClassName="fixed inset-0 z-[120] flex items-center justify-center bg-black bg-opacity-50 p-4"
      />
    </div>
  );
}
